import { Actor, HttpAgent } from "@dfinity/agent";
import type { Identity } from "@dfinity/agent";
import type { IDL } from "@dfinity/candid";
import type {
  IndexActorSubclass,
  ShopActorSubclass,
} from "./types";

type InterfaceFactory = IDL.InterfaceFactory;

const indexIdl: InterfaceFactory = ({ IDL }) => {
  const StoreRole = IDL.Variant({ Manager: IDL.Null, Cashier: IDL.Null });
  const StoreUser = IDL.Record({
    name: IDL.Text,
    principal: IDL.Principal,
    role: StoreRole,
  });
  const SimpleShopRecord = IDL.Record({
    id: IDL.Principal,
    name: IDL.Text,
    contacts: IDL.Opt(IDL.Text),
    address: IDL.Opt(IDL.Text),
    accountNumber: IDL.Text,
    created: IDL.Int,
  });
  const StaffShopAccess = IDL.Record({
    shop: IDL.Record({
      id: IDL.Principal,
      idx: IDL.Nat,
      name: IDL.Text,
    }),
    user: StoreUser,
  });
  const MyAccess = IDL.Record({
    ownedShops: IDL.Vec(SimpleShopRecord),
    staffShop: IDL.Opt(StaffShopAccess),
  });
  const IndexError = IDL.Variant({
    Anonymous: IDL.Null,
    AlreadyCreating: IDL.Null,
    AlreadyStoreUser: IDL.Null,
    InvalidInput: IDL.Text,
    NotFound: IDL.Null,
    NotAuthorized: IDL.Null,
    NotOwner: IDL.Null,
    FeeConfigError: IDL.Text,
    CanisterError: IDL.Text,
  });
  return IDL.Service({
    getMyAccess: IDL.Func(
      [],
      [IDL.Variant({ ok: MyAccess, err: IndexError })],
      ["query"],
    ),
  });
};

const shopIdl: InterfaceFactory = ({ IDL }) => {
  const ShopError = IDL.Variant({
    Anonymous: IDL.Null,
    NotOwner: IDL.Null,
    NotAuthorized: IDL.Null,
    NotFound: IDL.Null,
    InvalidInput: IDL.Text,
    ShopClosed: IDL.Null,
    HasEarnedCommission: IDL.Null,
    SubscriptionExpired: IDL.Null,
    CanisterError: IDL.Text,
  });
  const BusinessHours = IDL.Record({ open: IDL.Text, close: IDL.Text });
  const MpesaShortcode = IDL.Variant({
    Till: IDL.Text,
    Paybill: IDL.Record({ number: IDL.Text, account: IDL.Text }),
  });
  const ShopMeta = IDL.Record({
    name: IDL.Text,
    contacts: IDL.Opt(IDL.Text),
    address: IDL.Opt(IDL.Text),
    number: IDL.Nat,
    accountNumber: IDL.Text,
    owner: IDL.Principal,
    createdAt: IDL.Int,
    commissionOnSales: IDL.Bool,
    commissionRate: IDL.Nat,
    businessHours: IDL.Opt(BusinessHours),
    inventoryEnabled: IDL.Bool,
    stockManagementEnabled: IDL.Bool,
    mpesaShortcode: IDL.Opt(MpesaShortcode),
    customerManagementEnabled: IDL.Bool,
    expenseTrackingEnabled: IDL.Bool,
    multiPriceEnabled: IDL.Bool,
    etimsEnabled: IDL.Bool,
    etimsBusinessPin: IDL.Opt(IDL.Text),
  });
  const CashierSales = IDL.Record({
    cashier: IDL.Principal,
    cashierName: IDL.Opt(IDL.Text),
    totalSales: IDL.Nat,
    totalSalesKes: IDL.Nat,
  });
  const StewardCommission = IDL.Record({
    stewardId: IDL.Nat,
    totalSales: IDL.Nat,
    stewardName: IDL.Opt(IDL.Text),
    commissionKes: IDL.Nat,
  });
  const DailyCommission = IDL.Record({
    date: IDL.Text,
    totalSales: IDL.Nat,
    totalCommissionKes: IDL.Nat,
    stewards: IDL.Vec(StewardCommission),
  });
  const DailySales = IDL.Record({
    date: IDL.Text,
    totalSales: IDL.Nat,
    totalSalesKes: IDL.Nat,
    totalCashKes: IDL.Nat,
    totalMpesaKes: IDL.Nat,
    totalCardKes: IDL.Nat,
    totalCashSales: IDL.Nat,
    totalMpesaSales: IDL.Nat,
    totalCardSales: IDL.Nat,
    totalSplitSales: IDL.Nat,
    totalDiscountKes: IDL.Nat,
    totalFifoSalesKes: IDL.Opt(IDL.Nat),
    totalFifoCostKes: IDL.Opt(IDL.Nat),
    marginKes: IDL.Opt(IDL.Nat),
    marginPct: IDL.Opt(IDL.Nat),
    cashiers: IDL.Vec(CashierSales),
  });
  const DailyFilter = IDL.Record({
    fromDate: IDL.Opt(IDL.Text),
    toDate: IDL.Opt(IDL.Text),
    cursor: IDL.Opt(IDL.Text),
    limit: IDL.Nat,
  });
  const ReportDay = IDL.Record({
    date: IDL.Text,
    totalSales: IDL.Nat,
    totalSalesKes: IDL.Nat,
    totalCashKes: IDL.Nat,
    totalMpesaKes: IDL.Nat,
    totalCardKes: IDL.Nat,
    totalDiscountKes: IDL.Nat,
    totalFifoSalesKes: IDL.Opt(IDL.Nat),
    totalFifoCostKes: IDL.Opt(IDL.Nat),
    marginKes: IDL.Opt(IDL.Nat),
    marginPct: IDL.Opt(IDL.Nat),
  });
  const ProductSales = IDL.Record({
    productId: IDL.Nat,
    productVariantId: IDL.Opt(IDL.Nat),
    variantName: IDL.Opt(IDL.Text),
    name: IDL.Opt(IDL.Text),
    unit: IDL.Opt(IDL.Text),
    totalItems: IDL.Nat,
    totalSales: IDL.Nat,
    totalSalesKes: IDL.Nat,
    totalFifoSalesKes: IDL.Opt(IDL.Nat),
    totalFifoCostKes: IDL.Opt(IDL.Nat),
    marginKes: IDL.Opt(IDL.Nat),
    marginPct: IDL.Opt(IDL.Nat),
  });
  const CustomerSales = IDL.Record({
    id: IDL.Nat,
    name: IDL.Opt(IDL.Text),
    totalSales: IDL.Nat,
    totalSalesKes: IDL.Nat,
  });
  const ExpenseTagStats = IDL.Record({
    id: IDL.Nat,
    subTagName: IDL.Opt(IDL.Text),
    totalExpenses: IDL.Nat,
    totalExpensesKes: IDL.Nat,
    name: IDL.Opt(IDL.Text),
  });
  const ReportSales = IDL.Record({
    totalSales: IDL.Nat,
    totalSalesKes: IDL.Nat,
    totalCashKes: IDL.Nat,
    totalMpesaKes: IDL.Nat,
    totalCardKes: IDL.Nat,
    totalCashSales: IDL.Nat,
    totalMpesaSales: IDL.Nat,
    totalCardSales: IDL.Nat,
    totalSplitSales: IDL.Nat,
    totalDiscountKes: IDL.Nat,
    averageSaleKes: IDL.Nat,
    averageItemsPerSale: IDL.Opt(IDL.Nat),
    totalFifoSalesKes: IDL.Opt(IDL.Nat),
    totalFifoCostKes: IDL.Opt(IDL.Nat),
    marginKes: IDL.Opt(IDL.Nat),
    marginPct: IDL.Opt(IDL.Nat),
    bestDay: IDL.Opt(ReportDay),
    slowestDay: IDL.Opt(ReportDay),
    days: IDL.Vec(ReportDay),
  });
  const ShopReport = IDL.Record({
    fromDate: IDL.Text,
    toDate: IDL.Text,
    sales: ReportSales,
    products: IDL.Opt(
      IDL.Record({
        totalItems: IDL.Nat,
        topByRevenue: IDL.Vec(ProductSales),
        topByItems: IDL.Vec(ProductSales),
        topBySales: IDL.Vec(ProductSales),
        topByMargin: IDL.Vec(ProductSales),
        topByMarginKes: IDL.Vec(ProductSales),
      }),
    ),
    commissions: IDL.Opt(
      IDL.Record({
        totalCommissionKes: IDL.Nat,
        highestEarner: IDL.Opt(StewardCommission),
        topEarners: IDL.Vec(StewardCommission),
      }),
    ),
    staff: IDL.Opt(
      IDL.Record({
        topByRevenue: IDL.Vec(CashierSales),
        topBySales: IDL.Vec(CashierSales),
      }),
    ),
    customers: IDL.Opt(
      IDL.Record({
        totalUniqueCustomers: IDL.Nat,
        totalCustomerSalesKes: IDL.Nat,
        totalCustomerSales: IDL.Nat,
        averageSpendKes: IDL.Nat,
        newCustomers: IDL.Nat,
        repeatCustomers: IDL.Nat,
        topByRevenue: IDL.Vec(CustomerSales),
        topBySales: IDL.Vec(CustomerSales),
      }),
    ),
    expenses: IDL.Opt(
      IDL.Record({
        totalExpenses: IDL.Nat,
        totalExpensesKes: IDL.Nat,
        averageExpenseKes: IDL.Nat,
        topByAmount: IDL.Vec(ExpenseTagStats),
        topByCount: IDL.Vec(ExpenseTagStats),
      }),
    ),
  });
  const result = (value: IDL.Type<unknown>) =>
    IDL.Variant({ ok: value, err: ShopError });

  return IDL.Service({
    getShopMeta: IDL.Func([], [result(ShopMeta)], ["query"]),
    getReport: IDL.Func(
      [IDL.Record({ fromDate: IDL.Text, toDate: IDL.Text })],
      [result(ShopReport)],
      ["query"],
    ),
    listDailySales: IDL.Func(
      [DailyFilter],
      [
        result(
          IDL.Record({
            items: IDL.Vec(DailySales),
            nextCursor: IDL.Opt(IDL.Text),
          }),
        ),
      ],
      ["query"],
    ),
    listDailyCommissions: IDL.Func(
      [DailyFilter],
      [
        result(
          IDL.Record({
            items: IDL.Vec(DailyCommission),
            nextCursor: IDL.Opt(IDL.Text),
          }),
        ),
      ],
      ["query"],
    ),
  });
};

function isLocalHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".localhost")
  );
}

function getAgentHost(): string {
  if (typeof window !== "undefined") {
    if (isLocalHost(window.location.hostname)) {
      return `http://127.0.0.1:${window.location.port || "4943"}`;
    }
    return window.location.origin;
  }
  return import.meta.env.DFX_NETWORK === "ic"
    ? "https://ic0.app"
    : "http://127.0.0.1:4943";
}

export function getCanisterEnv(): Record<string, string> {
  if (typeof document === "undefined") return {};
  const raw = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith("ic_env="))
    ?.slice("ic_env=".length);
  if (!raw) return {};
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith("{")) return JSON.parse(decoded);
    return Object.fromEntries(new URLSearchParams(decoded).entries());
  } catch {
    return {};
  }
}

function hexToArrayBuffer(hex: string): ArrayBuffer | undefined {
  if (!hex || hex.length % 2 !== 0 || /[^0-9a-f]/i.test(hex)) return undefined;
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes.buffer;
}

async function makeAgent(identity: Identity): Promise<HttpAgent> {
  const env = getCanisterEnv();
  const rootKey = env.IC_ROOT_KEY || env.ic_root_key;
  return HttpAgent.create({
    identity,
    host: getAgentHost(),
    rootKey: rootKey ? hexToArrayBuffer(rootKey) : undefined,
  });
}

function getIndexCanisterId(): string {
  const id =
    import.meta.env.INDEX_CANISTER_ID ||
    getCanisterEnv()["PUBLIC_CANISTER_ID:tillflo_index"];
  if (!id) throw new Error("Missing tillflo_index canister id");
  return id;
}

export async function createIndexActor(
  identity: Identity,
): Promise<IndexActorSubclass> {
  const agent = await makeAgent(identity);
  return Actor.createActor(indexIdl, {
    agent,
    canisterId: getIndexCanisterId(),
  }) as IndexActorSubclass;
}

export async function createShopActor(
  canisterId: string,
  identity: Identity,
): Promise<ShopActorSubclass> {
  const agent = await makeAgent(identity);
  return Actor.createActor(shopIdl, { agent, canisterId }) as ShopActorSubclass;
}
